import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, TrendingDown, IndianRupee, Milestone as MilestoneIcon, Sparkles, CalendarRange, X } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import HorizonSelector from "@/components/HorizonSelector";
import CollectionsChart from "@/components/CollectionsChart";
import Sparkline from "@/components/Sparkline";
import CountUp from "@/components/CountUp";
import TypeMixDonut from "@/components/TypeMixDonut";
import ProjectDetailDrawer from "@/components/ProjectDetailDrawer";
import ExportXlsxButton from "@/components/ExportXlsxButton";
import { PageShell } from "@/components/SidebarLayout";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { useIsMobile } from "@/lib/useMediaQuery";
import { useProjectFilter } from "@/lib/ProjectFilterContext";
import { TEST_IDS } from "@/testIds";

const TYPES = [
  { value: "all", label: "All Types" },
  { value: "new", label: "New" },
  { value: "ongoing", label: "Ongoing" },
  { value: "land", label: "Land" },
];

/**
 * Inclusive month count between two ISO first-of-month dates. Example:
 * ("2026-07-01", "2027-06-01") → 12 months.
 */
function monthsBetween(startIso, endIso) {
  const s = new Date(startIso);
  const e = new Date(endIso);
  const n = (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()) + 1;
  return Math.max(1, n);
}

function StatCard({ label, value, numericValue, sub, sparkPoints, sparkTone = "ink", pill, testId, accent = "count", icon: Icon, format }) {
  const renderValue = () => {
    if (numericValue != null && format) {
      return <CountUp value={numericValue} format={format} duration={1000} />;
    }
    return value;
  };
  return (
    <div className={`cf-card cf-card-hover cf-fade-up cf-stat cf-accent-${accent}`}>
      {Icon && <div className="cf-stat-chip"><Icon size={16} /></div>}
      <div className="text-[11px] uppercase tracking-[0.14em] text-muted-light font-semibold mb-3">{label}</div>
      <div className="flex items-end justify-between gap-3">
        <div data-testid={testId} className="num leading-none text-ink" style={{ fontWeight: 700, fontSize: 26, letterSpacing: "-0.03em" }}>
          {renderValue()}
        </div>
        {pill}
      </div>
      <div className="cf-stat-underline" />
      <div className="mt-3 flex items-center justify-between gap-3">
        {sub != null && <div className="text-xs text-muted-dark">{sub}</div>}
        {sparkPoints && sparkPoints.length > 1 && <Sparkline points={sparkPoints} tone={sparkTone} width={78} height={22} />}
      </div>
    </div>
  );
}

function Pill({ children, tone = "ink" }) {
  const cls = { ok: "cf-pill cf-pill-ok", warn: "cf-pill cf-pill-warn", risk: "cf-pill cf-pill-risk", ink: "cf-pill cf-pill-ink" }[tone];
  return <span className={cls}>{children}</span>;
}

/**
 * Compact sales KPI card for the chairman's landing strip. Ink-on-cream by
 * default with an accent border-top so the row scans as a set.
 */
function SalesMiniKPI({ label, value, sub, accent = "ink", testId }) {
  const stripe = {
    ink: "var(--cf-ink)",
    coral: "var(--cf-accent)",
    emerald: "#10B981",
    indigo: "#6366F1",
  }[accent] || "var(--cf-ink)";
  return (
    <div
      data-testid={testId}
      className="glass-card p-4 min-w-0"
      style={{ borderTop: `2.5px solid ${stripe}` }}
    >
      <div className="text-[10px] uppercase tracking-widest text-muted-light font-semibold truncate">{label}</div>
      <div className="num text-lg md:text-2xl text-ink font-bold mt-1 truncate">{value}</div>
      {sub && <div className="text-[11px] text-muted-dark mt-1 truncate">{sub}</div>}
    </div>
  );
}

/**
 * Section heading used to visually separate Collections KPIs from Sales KPIs
 * on the landing page. Small kicker + inline colour dot pattern so the two
 * groups scan clearly as siblings.
 */
function SectionHeader({ title, subtitle, tone = "ink" }) {
  const dot = { ink: "var(--cf-ink)", coral: "var(--cf-accent)" }[tone] || "var(--cf-ink)";
  return (
    <div className="mb-3 flex items-center gap-2">
      <span style={{ width: 8, height: 8, borderRadius: 2, background: dot }} />
      <h2 className="font-display text-lg md:text-xl text-ink font-bold tracking-tight">{title}</h2>
      {subtitle && <span className="text-xs text-muted-dark hidden md:inline">· {subtitle}</span>}
    </div>
  );
}

/**
 * Combined bar chart — Sales bookings (coral) + Collections (indigo) side-
 * by-side per month. Answers the chairman's "of what we sold, when does it
 * come back as cash?" question in one glance.
 */
function SalesVsCollectionsChart({ timeline, salesVelocity, horizonStart }) {
  const salesByMonth = Object.fromEntries((salesVelocity || []).filter((v) => v.period === "projected").map((v) => [v.month, v.sales_value]));
  const data = (timeline || []).map((t) => ({
    month: t.month,
    sales: salesByMonth[t.month] ?? 0,
    collections: t.projected ?? 0,
  }));
  return (
    <div className="cf-card p-4 md:p-6">
      <div className="mb-3 md:mb-4 flex items-center justify-between">
        <div>
          <h3 className="font-display text-base md:text-lg text-ink font-bold">Sales vs Collections</h3>
          <p className="text-[11px] md:text-xs text-muted-dark">Per-month sales bookings paired with projected cash-in-hand</p>
        </div>
      </div>
      <div className="h-56 md:h-72">
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 10, right: 8, bottom: 8, left: -8 }}>
            <CartesianGrid stroke="rgba(18,49,89,0.10)" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: "var(--cf-muted-1)" }} tickFormatter={(m) => new Date(m).toLocaleDateString("en-IN", { month: "short", year: "2-digit" }).slice(0,3)} interval="preserveStartEnd" minTickGap={20} />
            <YAxis tick={{ fontSize: 10, fill: "var(--cf-muted-1)" }} tickFormatter={(v) => Math.round(v/100).toString()} width={32} />
            <Tooltip
              formatter={(v, name) => [formatMoney(v), name]}
              labelFormatter={(m) => new Date(m).toLocaleDateString("en-IN", { month: "long", year: "numeric" })}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="sales" name="Projected Sales" fill="var(--cf-accent)" radius={[4,4,0,0]} />
            <Bar dataKey="collections" name="Collections (projected)" fill="#6366F1" radius={[4,4,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[10px] text-muted-light mt-1">Y-axis in ₹ Cr</p>
    </div>
  );
}

export default function Projection() {
  const { project: projectFilter } = useProjectFilter();
  const [horizon, setHorizon] = useState(18);
  const [customRange, setCustomRange] = useState(null); // { start: "2026-07-01", end: "2027-06-01" }
  const [showCustom, setShowCustom] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [dashboard, setDashboard] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [sales, setSales] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawerProject, setDrawerProject] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      // Custom range overrides both as_of AND horizon so the API returns
      // exactly the [start .. end] window the user picked.
      const params = { horizon, ...(typeFilter !== "all" && { type: typeFilter }), ...(projectFilter !== "all" && { project: projectFilter }) };
      if (customRange) {
        params.as_of = customRange.start;
        params.horizon = monthsBetween(customRange.start, customRange.end);
      }
      const [dashRes, msRes, salesRes] = await Promise.all([
        api.get("/dashboard", { params }),
        api.get("/milestones"),
        api.get("/sales/analytics", { params: { horizon: params.horizon, ...(params.as_of && { as_of: params.as_of }) } }),
      ]);
      setDashboard(dashRes.data);
      setMilestones(msRes.data);
      setSales(salesRes.data);
    } catch (e) {
      toast.error("Failed to load projection", { description: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horizon, typeFilter, projectFilter, customRange]);

  const peak = useMemo(() => {
    if (!dashboard?.timeline?.length) return null;
    return dashboard.timeline.reduce((a, b) => (b.projected > a.projected ? b : a));
  }, [dashboard]);

  // Milestone events count = distinct custom milestones with demand_month inside the visible horizon window
  const milestoneCount = useMemo(() => {
    if (!dashboard?.timeline?.length) return 0;
    const months = new Set(dashboard.timeline.map((t) => t.month));
    return milestones.filter((m) =>
      months.has(m.demand_month) &&
      (typeFilter === "all" || true) && // milestones don't carry type; skip
      (projectFilter === "all" || m.project_name === projectFilter)
    ).length;
  }, [milestones, dashboard, projectFilter, typeFilter]);

  const timelinePoints = useMemo(() => (dashboard?.timeline || []).map((t) => t.projected), [dashboard]);
  const asOfLabel = dashboard ? new Date(dashboard.as_of).toLocaleDateString("en-IN", { month: "long", year: "numeric" }) : "…";

  // Type mix
  const typeMix = useMemo(() => {
    if (!dashboard?.projects) return { new: 0, ongoing: 0, land: 0, total: 0 };
    const m = { new: 0, ongoing: 0, land: 0 };
    dashboard.projects.forEach((p) => { m[p.project_type] = (m[p.project_type] || 0) + p.projected_total; });
    return { ...m, total: m.new + m.ongoing + m.land };
  }, [dashboard]);

  const trend = useMemo(() => {
    if (!dashboard?.timeline?.length) return null;
    const t = dashboard.timeline;
    const mid = Math.floor(t.length / 2);
    const first = t.slice(0, mid).reduce((s, x) => s + x.projected, 0);
    const second = t.slice(mid).reduce((s, x) => s + x.projected, 0);
    if (first === 0) return null;
    const delta = ((second - first) / first) * 100;
    return { delta, positive: delta >= 0 };
  }, [dashboard]);

  return (
    <PageShell
      testId="projection-page"
      kicker="Cashflow Simulation Engine"
      title="Projection"
      rangeMonths={dashboard?.timeline?.map((t) => t.month)}
      rangeLabel={customRange ? `${monthsBetween(customRange.start, customRange.end)}m · custom` : `${horizon}m`}
      actions={
        <div className="cf-hscroll -mx-4 md:mx-0 px-4 md:px-0 w-full md:w-auto">
          <div className="inline-flex items-center gap-2">
            <HorizonSelector
              value={horizon}
              onChange={(h) => { setHorizon(h); setCustomRange(null); }}
              months={dashboard?.timeline?.map((t) => t.month)}
            />
            <button
              data-testid="projection-custom-range-btn"
              onClick={() => setShowCustom(true)}
              className={`cf-touch inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold transition-colors ${customRange ? "bg-ink text-white" : "bg-surface/70 border border-white/70 text-ink"}`}
              title="Pick a custom start / end month"
            >
              <CalendarRange size={13} /> {customRange ? `${customRange.start.slice(0,7)} → ${customRange.end.slice(0,7)}` : "Custom"}
              {customRange && <X size={12} className="ml-1" onClick={(e) => { e.stopPropagation(); setCustomRange(null); }} />}
            </button>
            <ExportXlsxButton
              endpoint="/exports/projection.xlsx"
              filename={`projection_${horizon}m.xlsx`}
              params={{ horizon, type: typeFilter, project: projectFilter }}
              testId={TEST_IDS.exportProjectionBtn}
            />
          </div>
        </div>
      }
    >
      <p className="-mt-2 text-muted-dark text-sm max-w-2xl">
        Forward-looking receipts across new, ongoing and land developments —{" "}
        {dashboard ? <>{dashboard.horizon_months} months from {asOfLabel}.</> : "loading…"}
      </p>

      <div className="cf-hscroll -mx-4 md:mx-0 px-4 md:px-0">
        <div className="flex items-center gap-2 md:gap-3 min-w-max md:min-w-0 md:flex-wrap">
          <select
            data-testid={TEST_IDS.projectTypeFilter}
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="cf-touch px-3.5 py-2 bg-surface rounded-full text-sm text-ink focus:outline-none"
            style={{ boxShadow: "0 1px 2px rgba(18,49,89,0.04), 0 4px 10px rgba(18,49,89,0.05)" }}
          >
            {TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <div className="flex items-center gap-2 text-xs text-muted-dark px-2">
            <span className="cf-pulse" /><span>Live</span>
          </div>
          {loading && <span className="text-xs text-muted-light animate-pulse ml-2">Refreshing…</span>}
        </div>
      </div>

      {/* HERO CARD — projection only, with TypeMix donut instead of "% collected" */}
      <div className="cf-hero cf-fade-up p-6 md:p-10">
        <div className="relative flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/70 font-semibold mb-3">
              Projected · {dashboard?.horizon_months || horizon}-month horizon
            </div>
            <div className="flex items-baseline gap-3 flex-wrap">
              <div
                data-testid="hero-projected-total"
                className="num gradient-text"
                style={{ fontWeight: 900, fontSize: "clamp(44px, 8vw, 72px)", lineHeight: 0.95, letterSpacing: "-0.035em" }}
              >
                {dashboard ? <CountUp value={dashboard.kpi_projected_total} format={(v) => formatMoney(v)} duration={800} /> : "–"}
              </div>
              {trend && (
                <span
                  className="cf-pill"
                  style={{
                    background: trend.positive ? "rgba(18,183,106,0.22)" : "rgba(240,68,56,0.22)",
                    color: trend.positive ? "#B6F0CB" : "#FFC9C4",
                  }}
                >
                  {trend.positive ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                  {trend.positive ? "+" : ""}{trend.delta.toFixed(1)}% H2 vs H1
                </span>
              )}
            </div>
            <p className="mt-4 text-white/70 text-sm max-w-md leading-relaxed">
              Simulated receipts across the selected horizon. Model outflows and shortfalls on the <span className="text-white font-medium">Planning</span> page.
            </p>

            {/* Type-mix legend */}
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
              <LegendChip color="linear-gradient(90deg,#E11D48,#F43F5E,#FB7185)" label={`New · ${formatMoney(typeMix.new)}`} />
              <LegendChip color="#818CF8" label={`Ongoing · ${formatMoney(typeMix.ongoing)}`} />
              <LegendChip color="#34D399" label={`Land · ${formatMoney(typeMix.land)}`} />
            </div>
          </div>

          <div className="shrink-0 self-center md:self-auto relative">
            <TypeMixDonut
              size={168}
              stroke={16}
              segments={[
                { key: "new", value: typeMix.new },
                { key: "ongoing", value: typeMix.ongoing },
                { key: "land", value: typeMix.land },
              ]}
            />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center pointer-events-none">
              <div className="text-[10px] uppercase tracking-widest text-white/60">Type Mix</div>
              <div className="num font-bold text-white text-lg mt-0.5">{dashboard?.projects?.length || 0}</div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">Projects</div>
            </div>
          </div>
        </div>
      </div>

      {/* Stat cards — glass + colored chip + underline */}
      <div>
        <SectionHeader title="Collections" subtitle="Projected cash-in-hand across the horizon" tone="ink" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <StatCard accent="money" icon={IndianRupee} label="Projected · Horizon" numericValue={dashboard?.kpi_projected_total ?? 0} format={(v) => formatMoney(v)} sub={`${dashboard?.horizon_months || 0} months from ${dashboard ? new Date(dashboard.as_of).toLocaleDateString("en-IN", { month: "short", year: "2-digit" }) : "—"}`} sparkPoints={timelinePoints} sparkTone="coral" testId={TEST_IDS.kpiProjected} />
          <StatCard accent="positive" icon={TrendingUp} label="Peak Month" numericValue={peak?.projected ?? 0} format={(v) => formatMoney(v)} sub={peak ? peak.label : "—"} sparkPoints={timelinePoints} sparkTone="indigo" testId={TEST_IDS.kpiPeak} />
          <StatCard accent="count" icon={MilestoneIcon} label="Milestone Events" numericValue={milestoneCount || 0} format={(v) => Math.round(v).toString()} sub={milestoneCount ? "Within horizon" : "None in window"} testId={TEST_IDS.kpiMilestones} />
        </div>
      </div>

      {/* Sales section — sits directly under the Collections KPIs. Its own
          section heading + KPI strip so the two lenses are visually paired. */}
      {sales && (
        <div data-focus-hide="true">
          <SectionHeader title="Sales" subtitle="Top-line sales bookings across the same horizon" tone="coral" />
          <div data-testid="sales-kpi-strip" className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SalesMiniKPI label={`Projected Sales · ${horizon}m`} value={formatMoney(sales.kpi.total_sales)} accent="coral" testId="sales-kpi-total" />
            <SalesMiniKPI
              label="Sales → Projected Collection"
              value={`${Math.round((sales.kpi.sales_to_collection_ratio || 0) * 100)}%`}
              sub={`${formatMoney(sales.kpi.total_collections)} projected`}
              accent="emerald"
              testId="sales-kpi-ratio"
            />
            <SalesMiniKPI
              label="Top 3 Sellers · Share"
              value={`${Math.round((sales.kpi.top_3_sellers.reduce((a, b) => a + (b.share || 0), 0)) * 100)}%`}
              sub={sales.kpi.top_3_sellers.map((t) => t.project_name).join(" · ")}
              accent="ink"
              testId="sales-kpi-top3"
            />
            <SalesMiniKPI
              label="Sales Mix"
              value={`${Math.round((sales.kpi.type_mix.new?.share || 0) * 100)}% new`}
              sub={`${Math.round((sales.kpi.type_mix.ongoing?.share || 0) * 100)}% ongoing · ${Math.round((sales.kpi.type_mix.land?.share || 0) * 100)}% land`}
              accent="indigo"
              testId="sales-kpi-mix"
            />
          </div>
        </div>
      )}

      {/* Combined Sales-vs-Collections chart — two bar series per month. */}
      {dashboard && sales && (
        <div data-focus-hide="true">
          <SalesVsCollectionsChart timeline={dashboard.timeline} salesVelocity={sales.velocity} horizonStart={sales.months?.[0]} />
        </div>
      )}

      {dashboard && <div data-focus-hide="true"><CollectionsChart timeline={dashboard.timeline} projectionOnly /></div>}

      <div data-focus-hide="true">
        <ProjectsList
          projects={dashboard?.projects}
          onOpen={setDrawerProject}
          salesByProject={sales?.sales_by_project_month}
        />
      </div>

      <ProjectDetailDrawer
        open={!!drawerProject}
        project={drawerProject}
        months={dashboard?.timeline?.map((t) => t.month) || []}
        milestones={milestones.filter((m) => m.project_name === drawerProject?.project_name)}
        onClose={() => setDrawerProject(null)}
      />

      {showCustom && (
        <CustomRangeModal
          initial={customRange}
          onCancel={() => setShowCustom(false)}
          onApply={(range) => { setCustomRange(range); setShowCustom(false); }}
        />
      )}
    </PageShell>
  );
}

function CustomRangeModal({ initial, onCancel, onApply }) {
  const FLOOR = "2026-07";
  // Default start: current month (falls back to FLOOR when the clock is
  // still before the earliest seed month, e.g. during historical replays).
  const _today = new Date();
  const _todayIso = `${_today.getFullYear()}-${String(_today.getMonth() + 1).padStart(2, "0")}`;
  const _defaultStart = _todayIso >= FLOOR ? _todayIso : FLOOR;
  const [start, setStart] = useState((initial?.start || `${_defaultStart}-01`).slice(0, 7));
  const [end, setEnd] = useState((initial?.end || "2027-12-01").slice(0, 7));
  const startErr = start < FLOOR ? `Start must be July 2026 or later` : null;
  const endErr = end <= start ? `End must be after start` : null;
  return (
    <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <div className="glass-card max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg text-ink">Custom timeline</h3>
          <button onClick={onCancel} className="p-1.5 text-muted-dark hover:text-ink"><X size={14} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 text-xs">
          <label>
            <span className="block text-muted-dark mb-1">Start month</span>
            <input type="month" min={FLOOR} value={start} onChange={(e) => setStart(e.target.value)} className="w-full px-2 py-2 bg-surface rounded-lg num" />
            {startErr && <div className="mt-1 text-red-600">{startErr}</div>}
          </label>
          <label>
            <span className="block text-muted-dark mb-1">End month</span>
            <input type="month" min={FLOOR} value={end} onChange={(e) => setEnd(e.target.value)} className="w-full px-2 py-2 bg-surface rounded-lg num" />
            {endErr && <div className="mt-1 text-red-600">{endErr}</div>}
          </label>
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onCancel} className="px-3 py-2 text-xs text-muted-dark">Cancel</button>
          <button
            data-testid="projection-custom-range-apply"
            disabled={!!startErr || !!endErr}
            onClick={() => onApply({ start: start + "-01", end: end + "-01" })}
            className="px-4 py-2 text-sm rounded-full bg-ink text-white disabled:opacity-50"
          >Apply</button>
        </div>
      </div>
    </div>
  );
}

function LegendChip({ color, label }) {
  return (
    <div className="flex items-center gap-2 text-white/85">
      <span className="inline-block w-3.5 h-3.5 rounded-full" style={{ background: color }} />
      <span className="num text-xs">{label}</span>
    </div>
  );
}

function ProjectsList({ projects = [], onOpen, salesByProject = {} }) {
  const isMobile = useIsMobile();
  // Precompute per-project horizon sales totals from the sales analytics
  // response. The keys are project names; values are {month → amount}.
  const salesTotal = (name) => {
    const map = salesByProject?.[name];
    if (!map) return 0;
    return Object.values(map).reduce((a, b) => a + (b || 0), 0);
  };
  return (
    <div className="cf-card cf-fade-up overflow-hidden">
      <div className="px-5 md:px-6 py-4 flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-light font-semibold">Per-Project Sales + Projection</div>
          <div className="mt-1 font-display text-lg text-ink" style={{ fontWeight: 700 }}>{projects.length} projects · click to inspect</div>
        </div>
      </div>

      {!isMobile && (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-muted-light border-b border-hairline/70">
              <th className="text-left font-semibold px-6 py-3">Project</th>
              <th className="text-left font-semibold px-3 py-3">Type</th>
              <th className="text-right font-semibold px-3 py-3" style={{ color: "var(--cf-accent)" }}>Projected Sales · Horizon</th>
              <th className="text-right font-semibold px-3 py-3">Collections · Horizon</th>
              <th className="text-right font-semibold px-3 py-3">Milestones</th>
              <th className="text-left font-semibold px-6 py-3">Trend</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((p, i) => {
              const risk = p.milestone_months?.length === 0 && p.projected_total > 0;
              const sales = salesTotal(p.project_name);
              return (
                <tr
                  key={p.project_id}
                  data-testid={TEST_IDS.projectRow(p.project_name)}
                  onClick={() => onOpen?.(p)}
                  className={`border-b border-hairline/50 last:border-0 hover:bg-surface-2/70 transition-colors cursor-pointer cf-row-in ${risk ? "cf-row-warn" : ""}`}
                  style={{ animationDelay: `${i * 40}ms` }}
                >
                  <td className="px-6 py-4 font-semibold text-ink">{p.project_name}</td>
                  <td className="px-3 py-4 text-xs text-muted-dark capitalize">
                    <TypeDot type={p.project_type} />{p.project_type}
                  </td>
                  <td className="px-3 py-4 text-right num text-sm font-medium" style={{ color: "var(--cf-accent-deep)" }}>{formatMoney(sales)}</td>
                  <td className="px-3 py-4 text-right num text-sm text-ink font-medium">{formatMoney(p.projected_total)}</td>
                  <td className="px-3 py-4 text-right num text-sm text-muted-dark">{p.milestone_months?.length || "–"}</td>
                  <td className="px-6 py-4"><Sparkline points={p.monthly} tone="ink" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {isMobile && (
      <div className="divide-y divide-hairline/60">
        {projects.map((p, i) => {
          const risk = p.milestone_months?.length === 0 && p.projected_total > 0;
          const sales = salesTotal(p.project_name);
          return (
            <div
              key={p.project_id}
              data-testid={TEST_IDS.projectRow(p.project_name)}
              onClick={() => onOpen?.(p)}
              className={`px-5 py-4 cf-row-in cursor-pointer active:bg-paper/60 ${risk ? "cf-row-warn" : ""}`}
              style={{ animationDelay: `${i * 40}ms` }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-ink text-[15px]">{p.project_name}</div>
                  <div className="text-xs text-muted-dark mt-0.5 flex items-center gap-1.5">
                    <TypeDot type={p.project_type} /><span className="capitalize">{p.project_type}</span>
                    <span className="text-muted-light">·</span>
                    <span>{p.milestone_months?.length || 0} milestones</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="num text-[11px] font-semibold" style={{ color: "var(--cf-accent-deep)" }}>{formatMoney(sales)} projected sales</div>
                  <div className="num text-lg text-ink font-semibold">{formatMoney(p.projected_total)}</div>
                  <div className="mt-1 flex justify-end"><Sparkline points={p.monthly} tone="ink" width={78} height={20} /></div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

function TypeDot({ type }) {
  const bg = type === "new" ? "#F43F5E" : type === "ongoing" ? "#818CF8" : type === "land" ? "#34D399" : "#8B93A3";
  return <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ background: bg }} />;
}
